import React, { useState, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { apiService } from '../services/apiService';
import { notificationService } from '../services/notificationService';
import { DashboardStats, Document } from '../types';
import { useTheme } from '../contexts/ThemeContext';

const Dashboard: React.FC = () => {
  const { effectiveTheme } = useTheme();
  const [stats, setStats] = useState<DashboardStats>({
    documents: { total: 0, completed: 0, processing: 0, failed: 0 },
    queryable_documents: 0
  });
  const [recentDocuments, setRecentDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      
      // Load processing stats
      const processingStats = await apiService.getProcessingStats();
      
      // Load chat analytics for queryable documents
      const chatAnalytics = await apiService.getChatAnalytics();
      
      // Load recent documents
      const documents = await apiService.getDocuments();
      const recentDocs = documents.slice(0, 5);
      
      setStats({
        documents: processingStats.documents,
        queryable_documents: chatAnalytics.queryable_documents
      });
      
      setRecentDocuments(recentDocs);
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      notificationService.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const getFileIcon = (fileType: string) => {
    switch(fileType.toLowerCase()) {
      case 'pdf': return 'pdf';
      case 'docx': return 'word';
      case 'pptx': return 'ppt';
      case 'xlsx': return 'excel';
      default: return 'text';
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: '2rem' }}>
        {/* Header Skeleton */}
        <div style={{ marginBottom: '2rem' }}>
          <Skeleton height={30} width="30%" style={{ marginBottom: '0.5rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
          <Skeleton height={20} width="50%" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
        </div>

        {/* Stats Cards Skeleton */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              style={{
                background: 'var(--bg-color)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-lg)',
                padding: '1.5rem',
                textAlign: 'center',
                boxShadow: 'var(--shadow-sm)'
              }}
            >
              <Skeleton circle height={60} width={60} style={{ margin: '0 auto 1rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
              <Skeleton height={30} width="50%" style={{ marginBottom: '0.5rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
              <Skeleton height={20} width="70%" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
            </div>
          ))}
        </div>

        {/* Recent Activity Skeleton */}
        <div style={{
          background: 'var(--bg-color)',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <div style={{
            background: 'var(--surface-color)',
            padding: '1.5rem',
            borderBottom: '1px solid var(--border-color)'
          }}>
            <Skeleton height={25} width="40%" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
          </div>
          <div style={{ padding: '1.5rem' }}>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={index}
                  style={{
                    background: 'var(--surface-color)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-md)',
                    padding: '1.25rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Skeleton circle height={20} width={20} style={{ marginRight: '1rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                    <div>
                      <Skeleton height={15} width="150px" style={{ marginBottom: '0.25rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                      <Skeleton height={10} width="100px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <Skeleton height={20} width="80px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                    <Skeleton height={4} width="60px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0', fontWeight: 600 }}>
          Dashboard
        </h2>
        <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '1rem' }}>
          Overview of your document processing and AI analysis
        </p>
      </div>

      {/* Stats Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: 'var(--bg-color)',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-lg)',
          padding: '1.5rem',
          textAlign: 'center',
          transition: 'all var(--transition-normal)',
          boxShadow: 'var(--shadow-sm)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
        }}>
          <div style={{
            background: 'var(--surface-color)',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem'
          }}>
            <i className="bi bi-files" style={{ fontSize: '1.5rem', color: 'var(--primary-color)' }}></i>
          </div>
          <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0', fontWeight: 600 }}>
            {stats.documents.total}
          </h3>
          <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>Total Documents</p>
        </div>

        <div style={{
          background: 'var(--bg-color)',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-lg)',
          padding: '1.5rem',
          textAlign: 'center',
          transition: 'all var(--transition-normal)',
          boxShadow: 'var(--shadow-sm)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
        }}>
          <div style={{
            background: effectiveTheme === 'dark' ? 'rgba(16, 185, 129, 0.1)' : '#f0fdf4',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem'
          }}>
            <i className="bi bi-check-circle" style={{ fontSize: '1.5rem', color: 'var(--success-color)' }}></i>
          </div>
          <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0', fontWeight: 600 }}>
            {stats.documents.completed}
          </h3>
          <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>Completed</p>
        </div>

        <div style={{
          background: 'var(--bg-color)',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-lg)',
          padding: '1.5rem',
          textAlign: 'center',
          transition: 'all var(--transition-normal)',
          boxShadow: 'var(--shadow-sm)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
        }}>
          <div style={{
            background: effectiveTheme === 'dark' ? 'rgba(245, 158, 11, 0.1)' : '#fef3c7',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem'
          }}>
            <i className="bi bi-gear" style={{ fontSize: '1.5rem', color: 'var(--warning-color)' }}></i>
          </div>
          <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0', fontWeight: 600 }}>
            {stats.documents.processing}
          </h3>
          <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>Processing</p>
        </div>

        <div style={{
          background: 'var(--bg-color)',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-lg)',
          padding: '1.5rem',
          textAlign: 'center',
          transition: 'all var(--transition-normal)',
          boxShadow: 'var(--shadow-sm)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
        }}>
          <div style={{
            background: effectiveTheme === 'dark' ? 'rgba(14, 165, 233, 0.1)' : '#f0f9ff',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem'
          }}>
            <i className="bi bi-robot" style={{ fontSize: '1.5rem', color: '#0ea5e9' }}></i>
          </div>
          <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0', fontWeight: 600 }}>
            {stats.queryable_documents}
          </h3>
          <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>AI Ready</p>
        </div>
      </div>

      {/* Recent Activity */}
      <div style={{
        background: 'var(--bg-color)',
        border: '1px solid var(--border-color)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        boxShadow: 'var(--shadow-sm)'
      }}>
        <div style={{
          background: 'var(--surface-color)',
          padding: '1.5rem',
          borderBottom: '1px solid var(--border-color)'
        }}>
          <h5 style={{ color: 'var(--text-primary)', margin: 0, fontWeight: 600 }}>Recent Activity</h5>
        </div>
        <div style={{ padding: '1.5rem' }}>
          {recentDocuments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 0' }}>
              <i className="bi bi-inbox" style={{ fontSize: '2.5rem', color: 'var(--text-muted)' }}></i>
              <p style={{ color: 'var(--text-secondary)', marginTop: '1rem', fontSize: '1rem' }}>No recent activity</p>
              <small style={{ color: 'var(--text-muted)' }}>Upload some documents to get started</small>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {recentDocuments.map((doc) => {
                const uploadDate = new Date(doc.upload_timestamp).toLocaleDateString();
                const timeAgo = getTimeAgo(doc.upload_timestamp);
                
                return (
                  <div
                    key={doc.id}
                    style={{
                      background: 'var(--surface-color)',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-md)',
                      padding: '1.25rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all var(--transition-normal)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--card-color)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--surface-color)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <i className={`bi bi-file-earmark-${getFileIcon(doc.file_type)}`} style={{
                        fontSize: '1.25rem',
                        color: 'var(--primary-color)',
                        marginRight: '1rem'
                      }}></i>
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.95rem' }}>
                          {doc.original_filename}
                        </div>
                        <small style={{ color: 'var(--text-secondary)' }}>
                          {timeAgo} • {uploadDate}
                        </small>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{
                        background: doc.status === 'completed'
                          ? (effectiveTheme === 'dark' ? 'rgba(16, 185, 129, 0.2)' : '#dcfce7')
                          : doc.status === 'processing'
                          ? (effectiveTheme === 'dark' ? 'rgba(245, 158, 11, 0.2)' : '#fef3c7')
                          : (effectiveTheme === 'dark' ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2'),
                        color: doc.status === 'completed'
                          ? 'var(--success-color)'
                          : doc.status === 'processing'
                          ? 'var(--warning-color)'
                          : 'var(--danger-color)',
                        padding: '0.25rem 0.75rem',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'capitalize'
                      }}>
                        {doc.status}
                      </span>
                      {doc.status === 'processing' && (
                        <div style={{
                          width: '60px',
                          height: '4px',
                          background: 'var(--border-color)',
                          borderRadius: '2px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            height: '100%',
                            background: 'var(--primary-color)',
                            width: `${Math.round(doc.processing_progress * 100)}%`,
                            borderRadius: '2px',
                            transition: 'width 0.3s ease'
                          }}></div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
